/**
 * Functional component version of Anchor with proper ref forwarding for use with MUI components
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { omit, startsWith } from 'es-toolkit/compat';
import clsx from 'clsx';

import { isValidHref } from '@tock/shared/utils/hrefValidate';

export interface AnchorV2Props extends React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
> {
  'data-testid'?: string;
  reload?: boolean;
}

const httpPattern = /.*:.*/;

/**
 * AnchorV2 is a functional component that properly handles refs for use with MUI components.
 * It renders either a react-router Link for internal navigation or a standard anchor element
 * for external links and anchor links.
 */
const AnchorV2 = React.forwardRef<HTMLAnchorElement, AnchorV2Props>(function AnchorV2(
  { children, reload, onClick, ...rest },
  ref
) {
  const handleClickInternalUrl = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        onClick(event);
      }
    },
    [onClick]
  );

  const handleClickExternalUrl = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        onClick(event);
      }

      const href = rest.href;
      if (!BROWSER || !href || href === '#' || startsWith(href, 'tel:')) {
        return;
      }
    },
    [onClick, rest.href]
  );

  // prevent XSS attacks
  const sanitizedHref = isValidHref(rest.href) ? rest.href : '';

  const isOutbound = httpPattern.test(sanitizedHref || '');
  const isAnchorLink = sanitizedHref && sanitizedHref.startsWith('#');

  // Render react-router Link for internal navigation
  if (sanitizedHref && !isOutbound && !reload && !isAnchorLink) {
    const linkProps = {
      ...rest,
      className: clsx(rest.className, 'Anchor'),
      to: sanitizedHref,
      innerRef: ref, // react-router Link uses innerRef instead of ref
      onClick: handleClickInternalUrl,
    };
    delete linkProps.href;
    return <Link {...linkProps}>{children}</Link>;
  }

  // Render standard HTML anchor for outbound links and anchor links
  const linkProps = omit(
    rest,
    'reload',
    'trackingCategory',
    'role' // we omit any explicit role prop because <a> implicitly has a link role and that is what we want
  );

  return (
    <a
      ref={ref}
      {...linkProps}
      href={sanitizedHref}
      className={clsx(rest.className, 'Anchor')}
      rel={rest.target === '_blank' ? 'noopener' : undefined}
      onClick={isOutbound ? handleClickExternalUrl : handleClickInternalUrl}
    >
      {children}
    </a>
  );
});

export default AnchorV2;
